import React, { useState, useEffect, useCallback } from 'react';
import Button from './Button';

interface Question {
  _id: string;
  text?: string;
  question?: string; // Some questions might use 'question' instead of 'text'
  options: {
    _id: string;
    text: string;
    isCorrect?: boolean;
    score?: number;
  }[];
  explanation?: string;
  points?: number;
}

interface Quiz {
  _id: string;
  title: string;
  description: string;
  questions: Question[];
  category: string;
  difficulty: string;
  timeLimit?: number;
}

interface QuizComponentProps {
  quiz: Quiz;
  onSubmit: (answers: { questionId: string; selectedOptionId: string }[]) => void;
  timeLimit?: number;
}

const QuizComponent: React.FC<QuizComponentProps> = ({ quiz, onSubmit, timeLimit }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ questionId: string; selectedOptionId: string }[]>([]);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(
    timeLimit ? timeLimit * 60 : null
  );
  const currentQuestion = quiz.questions[currentQuestionIndex];
  const totalQuestions = quiz.questions.length;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  const handleSubmitQuiz = useCallback(() => {
    onSubmit(answers);
  }, [onSubmit, answers]);

  useEffect(() => {
    if (timeRemaining === null || timeRemaining === 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(timer);
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, handleSubmitQuiz]);

  const handleSelectOption = (optionId: string) => {
    const newAnswers = [...answers];
    const existingAnswerIndex = newAnswers.findIndex(
      (a) => a.questionId === currentQuestion._id
    );

    if (existingAnswerIndex >= 0) {
      newAnswers[existingAnswerIndex].selectedOptionId = optionId;
    } else {
      newAnswers.push({
        questionId: currentQuestion._id,
        selectedOptionId: optionId,
      });
    }

    setAnswers(newAnswers);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const getCurrentAnswer = () => {
    return answers.find((a) => a.questionId === currentQuestion._id)?.selectedOptionId || '';
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-3xl mx-auto">
      {/* Quiz Header */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
        <h2 className="text-xl font-bold">{quiz.title}</h2>
        <div className="flex items-center">
          <span className="text-sm font-medium mr-2">
            Spørgsmål {currentQuestionIndex + 1} af {totalQuestions}
          </span>
          {timeRemaining !== null && (
            <span className="bg-snudefryd-light-purple text-snudefryd-blue px-3 py-1 rounded-full text-sm font-medium">
              {formatTime(timeRemaining)}
            </span>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
        <div
          className="bg-snudefryd-blue h-2.5 rounded-full"
          style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
        ></div>
      </div>

      {/* Question */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4">{currentQuestion.text || currentQuestion.question}</h3>
        <div className="space-y-3">
          {currentQuestion.options.map((option) => (
            <div
              key={option._id}
              onClick={() => handleSelectOption(option._id)}
              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                getCurrentAnswer() === option._id
                  ? 'border-snudefryd-blue bg-snudefryd-light-purple bg-opacity-20'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center">
                <div
                  className={`w-5 h-5 rounded-full border flex items-center justify-center mr-3 ${
                    getCurrentAnswer() === option._id
                      ? 'border-snudefryd-blue bg-snudefryd-blue'
                      : 'border-gray-400'
                  }`}
                >
                  {getCurrentAnswer() === option._id && (
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  )}
                </div>
                <span>{option.text}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        <Button
          onClick={handlePrevQuestion}
          variant="outline"
          disabled={currentQuestionIndex === 0}
        >
          Forrige
        </Button>

        <div className="flex gap-3">
          {isLastQuestion ? (
            <Button
              onClick={handleSubmitQuiz}
              variant="primary"
              disabled={answers.length < totalQuestions}
            >
              Afslut Quiz
            </Button>
          ) : (
            <Button
              onClick={handleNextQuestion}
              variant="primary"
              disabled={!getCurrentAnswer()}
            >
              Næste
            </Button>
          )}
        </div>
      </div>

      {/* Question Counter */}
      <div className="mt-6 flex flex-wrap gap-2 justify-center">
        {quiz.questions.map((_, index) => {
          const isAnswered = answers.some((a) => a.questionId === quiz.questions[index]._id);
          const isCurrent = index === currentQuestionIndex;

          return (
            <button
              key={index}
              onClick={() => setCurrentQuestionIndex(index)}
              className={`w-8 h-8 rounded-full text-sm font-medium flex items-center justify-center
                ${
                  isCurrent
                    ? 'bg-snudefryd-blue text-white'
                    : isAnswered
                    ? 'bg-snudefryd-light-purple text-snudefryd-blue'
                    : 'bg-gray-100 text-gray-600'
                }
              `}
            >
              {index + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default QuizComponent; 